import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, UserPlus, Users } from 'lucide-react';
import { usersApi, assignmentsApi } from '../../../lib/api.js';
import { Button } from '../../../components/ui/Button.js';
import { Select } from '../../../components/ui/Select.js';
import { Modal } from '../../../components/ui/Modal.js';
import { SkeletonRow } from '../../../components/ui/Skeleton.js';
import { useToast } from '../../../components/ui/Toast.js';
import type { Assignment } from '@imza/shared';
import type { ApiError } from '../../../lib/api.js';

export const Route = createFileRoute('/_auth/admin/assignments')({
  component: AssignmentsPage,
});

function AssignmentsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);
  const [conflictInfo, setConflictInfo] = useState<{
    landlordId: number;
    agentId: number;
    existing: { agentId: number; agentName: string | null; assignedAt: string };
  } | null>(null);

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
  });

  const agents = (usersData?.items ?? []).filter(
    (u) => u.role === 'user' && u.active
  );

  const agentOptions = agents.map((a) => ({ value: a.id, label: a.name }));

  const { data: assignmentsData, isLoading } = useQuery({
    queryKey: ['assignments', selectedAgentId],
    queryFn: () => assignmentsApi.list({ agent_id: selectedAgentId ?? undefined }),
    enabled: selectedAgentId !== null,
  });

  const deleteAssignmentMutation = useMutation({
    mutationFn: (id: number) => assignmentsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
      toast('Assignment removed', 'success');
    },
    onError: (err: Error) => toast(err.message, 'error'),
  });

  const createAssignmentMutation = useMutation({
    mutationFn: ({ landlordId, agentId, force }: { landlordId: number; agentId: number; force: boolean }) =>
      assignmentsApi.create({ landlord_id: landlordId, agent_id: agentId }, force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
      toast('Assigned', 'success');
      setConflictInfo(null);
    },
    onError: (err: any) => {
      if (err?.statusCode === 409 && err?.body?.existingAssignment) {
        const existing = err.body.existingAssignment;
        setConflictInfo({
          landlordId: err.body.existingAssignment.landlordId ?? 0,
          agentId: selectedAgentId ?? 0,
          existing,
        });
      } else {
        toast(err.message, 'error');
      }
    },
  });

  const assignments = assignmentsData?.items ?? [];
  const selectedAgent = agents.find((a) => a.id === selectedAgentId);

  return (
    <div className="flex flex-col h-full">
      {/* Page header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div>
          <h1 className="text-xl font-bold text-primary-text">Assignments</h1>
          {selectedAgent && (
            <p className="text-xs text-secondary mt-0.5">
              Viewing {selectedAgent.name}'s assignments — {assignments.length} total
            </p>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col px-4 py-4 sm:px-6 gap-4">
        {/* Agent selector card */}
        <div className="bg-white border border-border rounded-xl p-4 flex items-center gap-3">
          <Users className="w-4 h-4 text-secondary flex-shrink-0" />
          <div className="flex-1 max-w-xs">
            <Select
              label=""
              placeholder="Choose an agent…"
              options={agentOptions as Array<{ value: string | number; label: string }>}
              value={selectedAgentId ?? ''}
              onChange={(e) => setSelectedAgentId(e.target.value ? Number(e.target.value) : null)}
              aria-label="Select agent to view assignments"
            />
          </div>
          {selectedAgent && (
            <p className="text-sm text-secondary hidden sm:block">
              Viewing assignments for <strong className="text-primary-text">{selectedAgent.name}</strong>
            </p>
          )}
        </div>

        {/* Assignments table card */}
        {selectedAgentId !== null && (
          <div className="bg-white border border-border rounded-xl overflow-hidden flex-1 flex flex-col">
            {/* Toolbar */}
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <p className="font-medium text-primary-text text-sm">
                {selectedAgent?.name}'s assignments
              </p>
              <span className="text-xs text-secondary bg-hover-bg px-2.5 py-0.5 rounded-full">
                {assignments.length} total
              </span>
            </div>

            {/* Column headers */}
            <div className="hidden sm:grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_160px_120px] px-4 py-2.5 bg-[#f7f8fc] border-b border-border text-[10px] font-semibold text-secondary uppercase tracking-wide">
              <span>Agent</span>
              <span>Landlord</span>
              <span>Assigned On</span>
              <span className="text-right">Actions</span>
            </div>

            {/* Rows */}
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} />)
              ) : assignments.length === 0 ? (
                <div className="py-12 text-center">
                  <UserPlus className="w-8 h-8 text-secondary mx-auto mb-2 opacity-40" />
                  <p className="text-sm text-secondary">No assignments for this agent</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {assignments.map((a) => (
                    <AssignmentRow
                      key={a.id}
                      assignment={a}
                      agentName={selectedAgent?.name}
                      onRemove={() => deleteAssignmentMutation.mutate(a.id)}
                      removing={deleteAssignmentMutation.isPending}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Conflict dialog */}
      <Modal
        open={conflictInfo !== null}
        onClose={() => setConflictInfo(null)}
        title="Assignment conflict"
        size="sm"
      >
        {conflictInfo && (
          <div className="space-y-4">
            <p className="text-sm text-primary-text">
              This landlord is already assigned to{' '}
              <strong>{conflictInfo.existing.agentName ?? 'another agent'}</strong>.
              Override and reassign?
            </p>
            <p className="text-xs text-secondary">
              Current assignment made on{' '}
              {new Date(conflictInfo.existing.assignedAt).toLocaleDateString()}.
              This action is logged.
            </p>
            <div className="flex gap-2">
              <Button
                variant="danger"
                size="sm"
                className="flex-1"
                loading={createAssignmentMutation.isPending}
                onClick={() =>
                  createAssignmentMutation.mutate({
                    landlordId: conflictInfo.landlordId,
                    agentId: conflictInfo.agentId,
                    force: true,
                  })
                }
              >
                Override assignment
              </Button>
              <Button variant="secondary" size="sm" className="flex-1" onClick={() => setConflictInfo(null)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function AssignmentRow({
  assignment,
  agentName,
  onRemove,
  removing,
}: {
  assignment: Assignment;
  agentName?: string;
  onRemove: () => void;
  removing: boolean;
}) {
  return (
    <div className="flex flex-col sm:grid sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_160px_120px] items-start sm:items-center gap-2 px-4 py-3 hover:bg-hover-bg transition-colors">
      {/* Agent column */}
      <div className="min-w-0">
        <p className="text-sm font-medium text-primary-text truncate">
          {agentName ?? '—'}
        </p>
        <p className="text-xs text-secondary sm:hidden">Agent</p>
      </div>
      {/* Landlord column */}
      <div className="min-w-0">
        <p className="font-medium text-primary-text text-sm truncate">
          {assignment.landlordName ?? `Landlord #${assignment.landlordId}`}
        </p>
        <p className="text-xs text-secondary sm:hidden">Landlord</p>
      </div>
      {/* Assigned on */}
      <div>
        <p className="text-xs text-secondary whitespace-nowrap">
          {new Date(assignment.assignedAt).toLocaleDateString()}
        </p>
      </div>
      {/* Actions */}
      <div className="flex justify-end">
        <button
          onClick={onRemove}
          disabled={removing}
          className="p-2 rounded-lg hover:bg-red-50 min-h-[36px] min-w-[36px] flex items-center justify-center transition-colors"
          aria-label="Remove assignment"
        >
          <Trash2 className="w-4 h-4 text-danger" />
        </button>
      </div>
    </div>
  );
}
